import { useEffect, useMemo, useRef, useState } from 'react'
import { Avatar, Box, FileInput, Flex } from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { useIsMutating } from '@tanstack/react-query'

import { ChangeAvatarButton } from '@/modules/profile/components/ChangeAvatarButton/ChangeAvatarButton'
import { useChangeAvatarMenuItems } from '@/modules/profile/components/EditProfileForm/EditProfileForm.constants'
import { editProfileFormSchema } from '@/modules/profile/components/EditProfileForm/EditProfileForm.schema'
import type { EditProfileFormSchema } from '@/modules/profile/components/EditProfileForm/EditProfileForm.types'
import { editProfileFormDefaultValues } from '@/modules/profile/components/EditProfileForm/EditProfileForm.values'

import { useUserStore } from '@/store/user.store'

import { Button } from '@/components/Button/Button'
import { ConfirmationModal } from '@/components/ConfirmationModal/ConfirmationModal'
import { DropdownMenu } from '@/components/DropdownMenu/DropdownMenu'
import { Fieldset } from '@/components/Fieldset/Fieldset'
import { FlexInputWrapper } from '@/components/FlexInputWrapper/FlexInputWrapper'
import { Textarea } from '@/components/Textarea/Textarea'
import { TextInput } from '@/components/TextInput/TextInput'
import { Tooltip } from '@/components/Tooltip/Tooltip'

import { uploadToS3 } from '@/utils/aws.utils'

import {
  useFileServiceFileControllerGetPresignedUrl,
  useUserServiceUserControllerDeleteUser,
  useUserServiceUserControllerEditProfile,
} from '@/__generated__/queries'
import { role, type UserData } from '@/__generated__/requests'

import { useLogout } from '@/hooks/useLogout.hook'

export function EditProfileForm() {
  const [avatar, setAvatar] = useState<UserData['avatar']>(null)
  const [file, setFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLButtonElement | null>(null)

  const [opened, { close, open }] = useDisclosure(false)
  const logout = useLogout()

  const user = useUserStore.use.user()
  const setUser = useUserStore.use.setUser()
  const currentRole = useUserStore.use.currentRole()

  const isAdvisor = user?.roles?.includes(role.ADVISOR)
  const isEmployee = user?.roles?.includes(role.EMPLOYEE)

  const isMutating = useIsMutating({
    exact: true,
  })

  const items = useChangeAvatarMenuItems(avatar, setAvatar, fileInputRef)

  const initialValues = useMemo(
    () => editProfileFormDefaultValues(user),
    [user],
  )

  const form = useForm<EditProfileFormSchema>({
    initialValues,
    validate: zodResolver(editProfileFormSchema),
  })

  const editProfileMutation = useUserServiceUserControllerEditProfile({
    onSuccess: data => {
      notifications.show({
        color: 'green',
        message: 'Profile has been updated',
        title: 'Profile updated successfully',
      })

      setUser(data)
    },
  })

  const getPresignedUrlMutation = useFileServiceFileControllerGetPresignedUrl({
    onSuccess: async data => {
      if (avatar && avatar.startsWith('data:image')) {
        try {
          await uploadToS3(avatar, data.url)

          editProfileMutation.mutate({
            requestBody: {
              ...form.values,
              avatar: data.key,
            },
          })
        } catch {
          notifications.show({
            color: 'red',
            message: 'Failed to upload image',
            title: 'Error',
          })
        }
      }
    },
  })

  const deleteAccountMutation = useUserServiceUserControllerDeleteUser({
    onSuccess: () => {
      logout()
    },
  })

  const handleSubmit = form.onSubmit(async values => {
    if (avatar && avatar.startsWith('data:image')) {
      const mimeType = avatar.split(';')[0].split(':')[1]

      await getPresignedUrlMutation.mutateAsync({
        requestBody: {
          filename: `avatar_${user?.id}.${mimeType.split('/')[1]}`,
        },
      })
    } else {
      editProfileMutation.mutate({
        requestBody: values,
      })
    }
  })

  useEffect(() => {
    if (user) {
      setAvatar(user?.avatar)
    }
  }, [user])

  useEffect(() => {
    form.setFieldValue('avatar', avatar)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avatar])

  useEffect(() => {
    if (file) {
      const reader = new FileReader()

      reader.onloadend = () => {
        setAvatar(reader.result as string)
      }

      reader.readAsDataURL(file)
    }
  }, [file])

  return (
    <>
      <Box component="form" noValidate onSubmit={handleSubmit}>
        <Fieldset legend="Profile image" variant="unstyled">
          <Box pos="relative" w={120}>
            <Avatar size={120} src={avatar} />

            <DropdownMenu
              button={<ChangeAvatarButton />}
              items={items}
              position="right-start"
              w={185}
            />
          </Box>

          <FileInput
            ref={fileInputRef}
            accept="image/*"
            onChange={setFile}
            style={{ display: 'none' }}
          />
        </Fieldset>

        <Fieldset legend="Personal information" variant="unstyled">
          <FlexInputWrapper
            leftInput={
              <TextInput
                label="First name"
                placeholder="Enter your name"
                {...form.getInputProps('firstName')}
              />
            }
            rightInput={
              <TextInput
                label="Last name"
                placeholder="Enter your last name"
                {...form.getInputProps('lastName')}
              />
            }
          />

          <FlexInputWrapper
            leftInput={
              <Box pos="relative">
                <TextInput
                  disabled
                  label="Work email"
                  placeholder="Enter your email address"
                  readOnly
                  {...form.getInputProps('email')}
                />

                <Tooltip label="Work email cannot be changed" />
              </Box>
            }
            rightInput={
              <TextInput
                label="Phone number (optional)"
                placeholder="Enter your phone number"
                {...form.getInputProps('phone')}
              />
            }
          />

          <FlexInputWrapper
            isLast
            leftInput={
              <TextInput
                label="Personal email (optional)"
                placeholder="Enter your personal email"
                {...form.getInputProps('personalEmail')}
              />
            }
          />
        </Fieldset>

        {currentRole === role.ADVISOR && isAdvisor && (
          <Fieldset legend="Bio">
            <FlexInputWrapper
              isLast
              leftInput={
                <Textarea
                  autosize
                  description="Tell us about yourself (max 700 characters)"
                  minRows={4}
                  placeholder="Enter your bio"
                  {...form.getInputProps('bio')}
                />
              }
            />
          </Fieldset>
        )}

        <Flex
          direction={{ base: 'column', md: 'row' }}
          gap={16}
          justify={{ base: 'center', md: 'flex-start' }}
          mt={{ base: 16, md: 100 }}
        >
          {currentRole === role.EMPLOYEE && isEmployee && (
            <Button onClick={open} variant="error">
              Delete account
            </Button>
          )}

          <Button loading={!!isMutating} type="submit">
            Save
          </Button>
        </Flex>
      </Box>

      <ConfirmationModal
        button={{ label: 'Delete', variant: 'error' }}
        description="Are you sure you want to delete your account?"
        isLoading={!!isMutating}
        onClose={close}
        onConfirm={() => deleteAccountMutation.mutate()}
        opened={opened}
        title="Delete account"
      />
    </>
  )
}
